import { FULLY_GROWN_LEVEL } from 'src/apps/LearningSession/EndOfSession/BlobEndOfSession/groupSummaryItems';

/** Ring stroke sits on the same donut as the static flower icons. */
export const RING_RADIUS = 11.46;
export const RING_STROKE = 1.08;
export const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
export const SOIL_Y = 17.6;

export const ringDashOffset = (portion: number): number =>
  RING_CIRCUMFERENCE * (1 - Math.min(1, Math.max(0, portion)));

export type FlowerPose = {
  /** Yellow progress around the ring, 0–1, starting at 12 o'clock. */
  ring: number;
  /** Y of the stem tip. Smaller is taller. */
  stemTop: number;
  /** Leaf size, 1 = fully opened. */
  leaf: number;
  /** Closed mint bud before the yellow bud appears. */
  mintBud: number;
  /** Yellow bud that opens into the bloom. */
  yellowBud: number;
  /** Open flower. Springs past 1 when it first blooms. */
  bloom: number;
};

/**
 * One pose per growth level. 0–2 lengthen the stem and open the leaves,
 * 3–5 form a bud, 6 is the open flower.
 */
export const FLOWER_POSES: readonly FlowerPose[] = [
  { ring: 0.045, stemTop: 14.15, leaf: 0.58, mintBud: 0, yellowBud: 0, bloom: 0 },
  { ring: 0.16, stemTop: 11.7, leaf: 0.76, mintBud: 0, yellowBud: 0, bloom: 0 },
  { ring: 0.3, stemTop: 9.7, leaf: 0.9, mintBud: 0, yellowBud: 0, bloom: 0 },
  { ring: 0.5, stemTop: 8.8, leaf: 1, mintBud: 0.9, yellowBud: 0, bloom: 0 },
  { ring: 0.74, stemTop: 8.5, leaf: 1, mintBud: 0, yellowBud: 0.74, bloom: 0 },
  { ring: 0.9, stemTop: 8.45, leaf: 1, mintBud: 0, yellowBud: 1, bloom: 0 },
  { ring: 1, stemTop: 8.5, leaf: 1, mintBud: 0, yellowBud: 0, bloom: 1 },
];

export const displayedGrowthLevel = (growthLevel: number): number => {
  if (!Number.isFinite(growthLevel) || growthLevel <= 0) return 0;
  if (growthLevel >= FULLY_GROWN_LEVEL) return FULLY_GROWN_LEVEL;
  return Math.floor(growthLevel);
};

export const poseForGrowthLevel = (growthLevel: number): FlowerPose =>
  FLOWER_POSES[displayedGrowthLevel(growthLevel)];

export const growthFlowerTestId = (growthLevel: number): string =>
  `gl${displayedGrowthLevel(growthLevel)}`;

/** The bloom pop plays only on the step that first reaches a full flower. */
export const shouldPopBloom = (fromLevel: number, toLevel: number): boolean =>
  displayedGrowthLevel(fromLevel) < FULLY_GROWN_LEVEL &&
  displayedGrowthLevel(toLevel) === FULLY_GROWN_LEVEL;

export const BLOOM_SPRING = {
  type: 'spring' as const,
  stiffness: 500,
  damping: 11,
  mass: 0.5,
};

/** Slight overshoot so the stem and leaves arrive with a small bounce. */
export const GROW_TRANSITION = {
  duration: 0.55,
  ease: [0.34, 1.28, 0.4, 1] as [number, number, number, number],
};

export const lerp = (from: number, to: number, progress: number): number =>
  from + (to - from) * progress;

/** `bloom` overrides the interpolated bloom so the opening flower can spring past fully open. */
export const lerpPose = (
  from: FlowerPose,
  to: FlowerPose,
  progress: number,
  bloom = lerp(from.bloom, to.bloom, progress),
): FlowerPose => ({
  ring: lerp(from.ring, to.ring, progress),
  stemTop: lerp(from.stemTop, to.stemTop, progress),
  leaf: lerp(from.leaf, to.leaf, progress),
  mintBud: lerp(from.mintBud, to.mintBud, progress),
  yellowBud: lerp(from.yellowBud, to.yellowBud, progress),
  bloom,
});
